import * as Sentry from '@sentry/nextjs'

import {
  assertSentryRuntimeConfig,
  dataCollection,
  getTracesSampleRate,
  isTelemetryEnabled,
  sanitizeBreadcrumb,
  sanitizeErrorEvent,
  sanitizeReplayRecordingEvent,
  sanitizeSpan,
} from './config'

/**
 * Recorded as placeholders, on top of Sentry's own `.sentry-block` and `[data-sentry-block]`.
 * rrweb skips every mutation inside a blocked element, and a data grid's body mounts and drops
 * whole rows of cells as it scrolls: serializing them took over half the main thread on a wide
 * Loads list. The text is masked anyway, so a replay loses only the cells' layout.
 */
export const REPLAY_BLOCK_SELECTORS = ['[data-slot="data-grid-table-body"]']

export type SentryClientInitOptions = {
  /** Public client DSN, statically referenced from `NEXT_PUBLIC_SENTRY_DSN` in app code. */
  dsn?: string
  /** Deployment environment name (e.g. develop, preview, staging, production). */
  environment?: string
  /** Immutable release, identical to the source-map upload release. */
  release?: string
  /** Enable Session Replay (admin only). Always fully masked. */
  replay?: boolean
  /** Raw `NEXT_PUBLIC_SENTRY_TELEMETRY` value; only an explicit 'true' enables telemetry. */
  telemetryEnabled?: string
  /** Explicit trace propagation targets. Defaults to same-origin relative requests only. */
  tracePropagationTargets?: (RegExp | string)[]
  /** Fixed tunnel path (admin: '/monitoring'). Omit for direct ingestion. */
  tunnel?: string
}

/**
 * Browser Sentry initialization with the shared privacy policy:
 * v10 default data collection, no user identity, sanitized requests/breadcrumbs/
 * spans, explicit trace propagation targets, and Replay with all text and
 * inputs masked and all media blocked when enabled.
 *
 * Missing config with telemetry disabled is a no-op: no init, no network.
 */
export const initSentryClient = (options: SentryClientInitOptions): void => {
  const enabled = isTelemetryEnabled(options.telemetryEnabled)

  if (!enabled) return

  assertSentryRuntimeConfig({ dsn: options.dsn, enabled })

  Sentry.init({
    beforeBreadcrumb: sanitizeBreadcrumb,
    beforeSend: sanitizeErrorEvent,
    beforeSendSpan: sanitizeSpan,
    dataCollection,
    debug: false,
    dsn: options.dsn,
    environment: options.environment,
    integrations: options.replay
      ? [
          Sentry.replayIntegration({
            // Sentry 11.0.0 exposes no final Replay event/envelope callback. This callback only
            // covers Replay integration custom frames; rrweb DOM frames rely on masking/blocking.
            beforeAddRecordingEvent: sanitizeReplayRecordingEvent,
            block: REPLAY_BLOCK_SELECTORS,
            blockAllMedia: true,
            maskAllInputs: true,
            maskAllText: true,
            networkCaptureBodies: false,
            networkRequestHeaders: [],
            networkResponseHeaders: [],
          }),
        ]
      : [],
    release: options.release,
    replaysOnErrorSampleRate: options.replay ? 1 : undefined,
    replaysSessionSampleRate: options.replay ? 0.1 : undefined,
    tracePropagationTargets: options.tracePropagationTargets ?? [/^\//],
    tracesSampleRate: getTracesSampleRate(options.environment),
    tunnel: options.tunnel,
  })
}
